import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import MomentUtils from '@date-io/moment';

import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import CasesFilters from '../../../components/cases/CasesFilters';
import CasesTable from '../../../components/cases/CasesTable';
import ConnectedCasesTable from '../../../components/cases/ConnectedCasesTable';
import CaseModal from '../../../components/cases/form/CaseModal';
import AvailableKitsModal from '../../../components/cases/modals/AvailableKitsModal';
import CasesCalendarView from '../../../components/cases/CasesCalendarView';
import Input from '../../../components/shared/Input';
import LoadScreen from '../../../components/load-screen';
import { ConfirmationModal } from '../../../components/shared/modal';
import Button from '../../../components/shared/Button';
import Tabs from '../../../components/shared/Tabs';
import KitFiltersList from '../../../components/kits/KitFiltersList';
import KitsFiltersModal from '../../../components/kits/KitsFiltersModal';

import {
  bulkDeleteCases, createCase, toggleCanceled, setStatusFilter, setFormStep, getCases,
  toggleConsignment, toggleLoan, toggleShipped, setFilters, toggleReturned,
  setOrder, setOrderBy, setPage, setRowsPerPage, setSearch, toggleClosed, setSelectedDate,
  getSetsAllocation,
} from '../../../actions/casesActions';
import { getSurgeons, getTransferredSurgeons } from '../../../actions/surgeonsActions';
import { getProcedures, getProcedureOptions } from '../../../actions/proceduresActions';
import { getProducts } from '../../../actions/productsActions';
import { getKits } from '../../../actions/kitsActions';
import { getFlows } from '../../../actions/flowsActions';
import { getSets } from '../../../actions/setsActions';
import { getTenants } from '../../../actions/tenantActions';
import { getSurgeonsList } from '../../../reducers/selectors';

import { withFirebase } from '../../../firebase';

import { useAlert, useLoading, useDebounce } from '../../../hooks';

import { caseStatusOptions, kitVariantTypes } from '../../../constants/enums';
import userRoles from '../../../constants/userRoles';
import routes from '../../../constants/routes';
import alertTypes from '../../../constants/alertTypes';
import { CASE_DELETE_ALLOCATED_SETS_WARNING, hasAllocatedSetsForDelete } from '../../../utils/cases';

import './cases-page.scss';

moment.updateLocale('en', {
  week: {
    dow: 1
  }
});

const tabs = [
  { label: 'My Company' },
  { label: 'Connections' }
];

const views = {
  TABLE: 'TABLE',
  CALENDAR: 'CALENDAR',
};

const CasesPage = ({ firebase }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading } = useLoading();
  const { showAlert } = useAlert();

  const userRole = useSelector((state) => state.user.currentUser.role);
  const userAddresses = useSelector((state) => state.user.currentUser.deliveryAddresses);
  const connectedTenants = useSelector((state) => state?.user?.currentUser?.connectedTenants) || [];
  const caseWriteDisabled = useSelector((state) => state.user.currentUser.caseWriteDisabled);
  // const cases = useSelector(getCasesList);
  // const completedLoaded = useSelector((state) => state.cases.completedLoaded);
  const surgeons = useSelector(getSurgeonsList);
  const assignedCases = useSelector((state) => state.cases.assignedCases);
  const transferRequests = useSelector((state) => state.cases.transferRequests);
  const hospitals = useSelector((state) => state.hospitals.list);
  const procedures = useSelector((state) => state.procedures.list);
  const procedureOptions = useSelector((state) => state.procedures.procedureOptions);
  const products = useSelector((state) => state.products.list);
  const kits = useSelector((state) => state.kits.list);
  // const items = useSelector((state) => state.items.list);
  const showCanceled = useSelector((state) => state.cases.showCanceled);
  const flows = useSelector((state) => state.flows.list);
  const sets = useSelector((state) => state.sets.list?.filter((set) => !!set.active));
  const statusFilter = useSelector((state) => state.cases.statusFilter);
  const currentTenant = useSelector((state) => state.tenant.currentTenant);
  const tenants = useSelector((state) => state.tenant.list);

  const hideShipped = useSelector((state) => state.cases.hideShipped);
  const hideReturned = useSelector((state) => state.cases.hideReturned);
  const hideConsignment = useSelector((state) => state.cases.hideConsignment);
  const hideLoan = useSelector((state) => state.cases.hideLoan);
  const hideClosed = useSelector((state) => state.cases.hideClosed);
  const filters = useSelector((state) => state.cases.filters);
  const order = useSelector((state) => state.cases.order);
  const orderBy = useSelector((state) => state.cases.orderBy);
  const page = useSelector((state) => state.cases.page);
  const rowsPerPage = useSelector((state) => state.cases.rowsPerPage);
  const search = useSelector((state) => state.cases.search);
  const selectedDate = useSelector((state) => state.cases.selectedDate);

  const [cases, setCases] = useState([]);
  const [calendarCases, setCalendarCases] = useState([]);
  const [checkedCases, setCheckedCases] = useState([]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [isDeleting, toggleDeleting] = useState(false);

  const [caseModalOpen, toggleCaseModal] = useState(false);
  const [deleteModalOpen, toggleDeleteModal] = useState(false);

  const [availableModalData, setAvailableModalData] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [connectedCases, setConnectedCases] = useState([]);
  const [fetching] = useState(false);
  const [casesLoading, setCasesLoading] = useState(false);

  const [isOpen, toggleModal] = useState(false);
  const [view, setView] = useState(views.TABLE);
  const [totalRows, setTotalRows] = useState(0);
  const [totalConnectedRows, setTotalConnectedRows] = useState(0);

  const [, setIsLoaded] = useState(false);

  const debouncedSearch = useDebounce(search, 600);
  const debouncedFilters = useDebounce(filters, 200);

  useEffect(() => {
    onLoad().catch((err) => console.error(err));

    if (userRole === userRoles.SALES_REP.name && !!connectedTenants?.length) {
      dispatch(setFormStep('company'));
    }
  }, [userRole, connectedTenants]);

  useEffect(() => {
    dispatch(getTransferredSurgeons([...assignedCases, ...transferRequests]));
  }, [assignedCases, transferRequests]);

  useEffect(() => {
    dispatch(setPage(0));
  }, [hideShipped, hideReturned, hideConsignment, hideClosed, showCanceled, hideLoan, filters]);

  useEffect(() => {
    fetchCases().catch();
    if (userRole === userRoles.SALES_REP.name) {
      fetchConnectedCases().catch();
    }
  }, [page, rowsPerPage, order, orderBy, debouncedSearch, debouncedFilters, statusFilter, hideLoan, hideConsignment, hideClosed, selectedDate, showCanceled, dateFrom, dateTo]);

  useEffect(() => {
    if (view === views.CALENDAR) {
      const _dateFrom = moment().startOf('month').valueOf();
      const _dateTo = moment().endOf('month').valueOf();
      setDateFrom(dateFrom);
      setDateTo(dateTo);
      onDateChange(_dateFrom, _dateTo);
    } else {
      setDateFrom(null);
      setDateTo(null);
      // fetchCases();
    }
  }, [view]);

  useEffect(() => {
    onMonthChange(moment()).catch();
  }, []);

  const onLoad = async () => {
    // startLoading()
    try {
      await Promise.all([
        dispatch(getSurgeons()),
        dispatch(getProcedures()),
        dispatch(getProcedureOptions()),
        dispatch(getProducts()),
        dispatch(getKits()),
        dispatch(getFlows()),
        dispatch(getSets()),
        dispatch(getTenants()),
      ]);
      setIsLoaded(true);
    } catch (err) {
      console.error(err);
    } finally {
      // stopLoading();
    }
  };

  const fetchCases = async () => {
    try {
      setCasesLoading(true);

      const statuses = Object.keys(statusFilter).filter((key) => !!statusFilter[key]).map((key) => key?.toUpperCase());
      let kitVariants = [kitVariantTypes.loan, kitVariantTypes.consignment];

      if (hideLoan) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.loan);
      }

      if (hideConsignment) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.consignment);
      }

      if (hideLoan && hideConsignment) {
        setCases([]);
        setTotalRows(0);
        return;
      }

      const params = {
        search: debouncedSearch || '*',
        sortBy: orderBy || 'date',
        sortOrder: order || 'asc',
        page: page + 1, // page starts from 1
        perPage: rowsPerPage,
        active: true,
        statuses,
        kitVariants,
        hospitals: debouncedFilters.hospitals,
        states: debouncedFilters.states,
        surgeons: debouncedFilters.surgeons,
        procedures: debouncedFilters.procedures,
      };

      if (showCanceled) {
        delete params.active;
      }

      if (hideClosed) {
        params.orderClosed = false;
      }

      if (selectedDate) {
        params.dateFrom = moment(selectedDate).startOf('day').valueOf();
        params.dateTo = moment(selectedDate).endOf('day').valueOf();
      }

      if (dateFrom && dateTo) {
        params.dateFrom = moment(dateFrom).startOf('day').valueOf();
        params.dateTo = moment(dateTo).endOf('day').valueOf();
        params.perPage = 250;
      }

      const res = await dispatch(getCases(params));
      if (res && res.cases) {
        setCases(res.cases);
        setTotalRows(res.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  };

  const onDateChange = async (dateFrom, dateTo) => {
    try {
      setCasesLoading(true);

      setDateFrom(dateFrom);
      setDateTo(dateTo);

      dispatch(setPage(0));

      const statuses = Object.keys(statusFilter).filter((key) => !!statusFilter[key]).map((key) => key?.toUpperCase());
      let kitVariants = [kitVariantTypes.loan, kitVariantTypes.consignment];

      if (hideLoan) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.loan);
      }

      if (hideConsignment) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.consignment);
      }

      if (hideLoan && hideConsignment) {
        setCases([]);
        setTotalRows(0);
        return;
      }

      const params = {
        search: debouncedSearch || '*',
        sortBy: orderBy || 'date',
        sortOrder: order || 'asc',
        active: true,
        statuses,
        kitVariants,
        hospitals: debouncedFilters.hospitals,
        states: debouncedFilters.states,
        surgeons: debouncedFilters.surgeons,
        procedures: debouncedFilters.procedures,
        dateFrom: moment(dateFrom).startOf('day').valueOf(),
        dateEnd: moment(dateFrom).startOf('day').valueOf(),
        perPage: 250,
      };

      if (showCanceled) {
        delete params.active;
      }

      if (hideClosed) {
        params.orderClosed = false;
      }

      const res = await dispatch(getCases(params));
      if (res && res.cases) {
        setCases(res.cases);
        setTotalRows(res.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  };

  const onChangeMinimizedCalendarDate = (value) => {
    dispatch(setPage(0));
    dispatch(setSelectedDate(value ? moment(value).valueOf() : null));
  };

  const onMonthChange = async (month) => {
    try {
      const dateFrom = moment(month).startOf('month').valueOf();
      const dateEnd = moment(month).endOf('month').valueOf();

      const params = {
        search: '*',
        active: true,
        dateFrom,
        dateEnd,
        perPage: 250,
      };

      const res = await dispatch(getCases(params));
      if (res && res.cases) {
        setCalendarCases(res.cases || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  }

  const fetchConnectedCases = async () => {
    try {
      setCasesLoading(true);

      const statuses = Object.keys(statusFilter).filter((key) => !!statusFilter[key]).map((key) => key?.toUpperCase());
      let kitVariants = [kitVariantTypes.loan, kitVariantTypes.consignment];

      if (hideLoan) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.loan);
      }

      if (hideConsignment) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.consignment);
      }

      if (hideLoan && hideConsignment) {
        setCases([]);
        setTotalRows(0);
        return;
      }

      const params = {
        search: debouncedSearch || '*',
        sortBy: orderBy || 'date',
        sortOrder: order || 'asc',
        page: page + 1, // page starts from 1
        perPage: rowsPerPage,
        active: true,
        statuses,
        kitVariants,
        hospitals: debouncedFilters.hospitals,
        states: debouncedFilters.states,
        surgeons: debouncedFilters.surgeons,
        procedures: debouncedFilters.procedures,
      };

      if (showCanceled) {
        delete params.active;
      }

      if (hideClosed) {
        params.orderClosed = false;
      }

      if (selectedDate) {
        params.dateFrom = moment(selectedDate).startOf('day').valueOf();
        params.dateTo = moment(selectedDate).endOf('day').valueOf();
      }

      const res = await dispatch(getCases(params, true));
      if (res && res.cases) {
        setConnectedCases(res.cases);
        setTotalConnectedRows(res.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  };

  const handleSearch = (e) => {
    // debouncedOnChange(e.target.value);
    dispatch(setPage(0));
    dispatch(setSearch(e.target.value));
  };

  const onSelectCase = (row, connected) => {
    history.push(`${routes.CASES}/${row.caseId}${connected ? `?tenantId=${row.tenantId}`: ''}`);
  };

  const onAddCaseClick = () => {
    toggleCaseModal(true);
  };

  const onAddCase = async (formData, isConnected) => {
    try {
      if (isConnected) {
        await dispatch(createCase(formData, isConnected));
        await fetchConnectedCases();
        showAlert(alertTypes.SUCCESS, 'Case has been successfully created');
        toggleCaseModal(false);
      } else {
        const caseId = await dispatch(createCase(formData));
        firebase.logEvent('case_created');
        showAlert(alertTypes.SUCCESS, 'Case has been successfully created');

        const warningKits = await checkKitAvailability(formData);

        if (warningKits?.length) {
          setAvailableModalData({ id: caseId, kits: warningKits });
        } else {
          if (caseId) {
            history.push(`${routes.CASES}/${caseId}`);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const checkKitAvailability = async (data) => {
    let list = cases?.filter((item) =>
      item?.status === caseStatusOptions.booked
      && moment(item?.date)?.isSame(moment(data?.date), 'day')
      && item?.kits?.some((kitId) => data?.kits?.map((kit) => kit.id)?.includes(kitId))
    );
    const result = [];

    if (data?.kitVariant === kitVariantTypes.consignment) {
      list = list?.filter((item) => item.kitVariant === kitVariantTypes.consignment);
    } else {
      list = list?.filter((item) => item.kitVariant === kitVariantTypes.loan);
    }

    data?.kits?.forEach((kit) => {
      let count = 1;
      list?.forEach((item) => {
        if (item?.kits?.includes(kit.id)) {
          count++;
        }
      });

      let kitSets = [];
      if (data?.kitVariant === kitVariantTypes.consignment) {
        kitSets = sets?.filter((set) => set?.kit === kit.id && set.consigned && set?.consignment?.hospital === data?.hospital);
      } else {
        kitSets = sets?.filter((set) => set?.kit === kit.id && !set?.consigned);
      }

      if (!!kitSets.length && count > kitSets?.length && !result?.includes(kit.id)) {
        result?.push(kit.id);
      }
    });

    return result;
  };

  const onContinueClick = (caseId) => {
    history.push(`${routes.CASES}/${caseId}`);
  };

  // const handleDeleteClick = () => {
  //   if (checkedCases.length) {
  //     toggleDeleteModal(true);
  //   }
  // };

  const onDeleteCases = async () => {
    toggleDeleting(true);

    try {
      const selectedSetsAllocation = await Promise.all(
        checkedCases?.map((caseId) => dispatch(getSetsAllocation(caseId, true))) || []
      );

      if (selectedSetsAllocation?.some((setsAllocation) => hasAllocatedSetsForDelete(setsAllocation))) {
        showAlert(alertTypes.WARNING, CASE_DELETE_ALLOCATED_SETS_WARNING);
        return;
      }

      await dispatch(bulkDeleteCases(checkedCases));

      toggleDeleteModal(false);
      setCheckedCases([]);
    } catch (err) {
      console.error(err);
      showAlert(alertTypes.WARNING, err.message || CASE_DELETE_ALLOCATED_SETS_WARNING);
    } finally {
      toggleDeleting(false);
    }
  };

  const onStatusFilterChange = async (key) => {
    dispatch(setPage(0));
    dispatch(setStatusFilter({ [key]: !statusFilter[key] }))
    // if (key === 'completed') {
    //   dispatch(setStatusFilter({ completed: !statusFilter.completed }));
    //   await dispatch(getCompletedCases());
    // } else {
    //   dispatch(setStatusFilter({ [key]: !statusFilter[key] }))
    // }
  };

  const onToggle = (value) => {
    dispatch(setPage(0));
    dispatch(toggleCanceled(value))
  };

  const getSurgeonName = useCallback((surgeonId, surgeonName = '') => {
    const surgeon = surgeons?.find((surgeon) => surgeon.id === surgeonId);
    return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : surgeonName;
  }, [surgeons]);

  const getHospitalName = useCallback((hospitalId, hospitalName = '') => {
    const hospital = hospitals?.find((hospital) => hospital.id === hospitalId);
    return hospital ? hospital.name : hospitalName;
  }, [hospitals]);

  const getHospitalState = useCallback((hospitalId, hospitalState = '') => {
    const hospital = hospitals?.find((hospital) => hospital.id === hospitalId);
    return hospital ? hospital.state : hospitalState;
  }, [hospitals]);

  const getProcedureName = useCallback((procedureId, procedureName = '') => {
    const procedure = procedures?.find((procedure) => procedure.id === procedureId);
    return procedure ? procedure.name : procedureName;
  }, [procedures]);

  const casesList = useMemo(() => {
    // const list = cases.filter((item) => filterCases(item, statusFilter, search, date));
    //
    // let result = [...list];
    //
    // if (hideConsignment) {
    //   result = result.filter((item) => item.kitVariant !== kitVariantTypes.consignment);
    // }
    //
    // if (hideLoan) {
    //   result = result.filter((item) => item.kitVariant !== kitVariantTypes.loan);
    // }
    //
    // if (hideClosed) {
    //   result = result.filter((item) => !item.orderClosed);
    // }
    //
    // if (filters.surgeons?.length) {
    //   result = result.filter((item) => filters.surgeons?.includes(item.surgeon));
    // }
    //
    // if (filters.hospitals?.length) {
    //   result = result.filter((item) => filters.hospitals?.includes(item.hospital));
    // }
    //
    // if (filters.states?.length) {
    //   const stateHospitals = hospitals?.filter((h) => filters.states?.includes(h.state));
    //   result = result.filter((item) => stateHospitals?.map((h) => h.id)?.includes(item.hospital));
    // }

    let result = [...cases];

    result = result.map((item) => ({
      ...item,
      surgeonName: getSurgeonName(item.surgeon, item.surgeonName),
      hospitalName: getHospitalName(item.hospital, item.hospitalName),
      hospitalState: getHospitalState(item.hospital, item.hospitalState),
      procedureName: getProcedureName(item.procedure, item.procedureName),
    }))

    // if (showCanceled) {
    //   return result;
    // }

    return result;
  }, [
    cases, statusFilter, search, selectedDate, showCanceled, search, cases,
    hideConsignment, hideLoan, hideClosed, filters, hospitals,
    getSurgeonName, getHospitalName, getHospitalState, getProcedureName,
  ]);

  const handleCheck = (caseId) => {
    const checked = checkedCases.slice();

    if (checked.includes(caseId)) {
      const index = checked.indexOf(caseId);

      checked.splice(index, 1);
      setCheckedCases(checked);

      return;
    }

    checked.push(caseId);
    setCheckedCases(checked);
  };

  const handleCheckAll = () => {
    const checked = checkedCases.length === casesList.length;
    const temp = [];

    if (!checked) {
      casesList.forEach((item) => {
        temp.push(item.id);
      });

      setCheckedCases(temp);
      return;
    }

    setCheckedCases(temp);
  };

  const onTabChange = async (tab) => {
    if (tab === 0) {
      setActiveTab(0);
    }
    if (tab === 1) {
      setActiveTab(1);
      await fetchConnectedCases();
    }
    setCheckedCases([]);
  };

  const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => {
    const className = getClassName(day);

    return <div className={`MuiDatepickerDay-container ${className}`}>
      {dayComponent}
    </div>;
  };

  const onRemoveFilter = (filter, value) => {
    dispatch(setPage(0));

    if (filter === 'consignment') {
      dispatch(toggleConsignment());
    }
    if (filter === 'loan') {
      dispatch(toggleLoan());
    }
    if (filter === 'shipped') {
      dispatch(toggleShipped());
    }
    if (filter === 'surgeon') {
      dispatch(setFilters({ surgeons: [...filters?.surgeons].filter((surgeon) => surgeon !== value) }));
    }
    if (filter === 'hospital') {
      dispatch(setFilters({ hospitals: [...filters?.hospitals].filter((hospital) => hospital !== value) }));
    }
    if (filter === 'state') {
      dispatch(setFilters({ states: [...filters?.states].filter((state) => state !== value) }));
    }
    if (filter === 'canceled') {
      dispatch(toggleCanceled(false));
    }
    if (filter === 'closed') {
      dispatch(toggleClosed(false));
    }
  };

  const getClassName = (day) => {
    // const list = activeTab === 1 ? connectedCases : cases;
    // const list = showCanceled ? casesList : casesList.filter((item) => !!item.active);
    const list = calendarCases;

    if (list.find((item) => moment(item.date).isSame(day, 'day') && item.status === caseStatusOptions.overdue)) {
      return 'overdue';
    }
    if (list.find((item) => moment(item.date).isSame(day, 'day') && item.status === caseStatusOptions.request)) {
      return 'request';
    }
    if (list.find((item) => moment(item.date).isSame(day, 'day') && item.status === caseStatusOptions.booked)) {
      return 'booked';
    }
    if (list.find((item) => moment(item.date).isSame(day, 'day') && item.status === caseStatusOptions.completed)) {
      return 'completed';
    }
    return '';
  };

  const isDisabled = useMemo(() => {
    if (checkedCases && checkedCases?.length) {
      const _cases = checkedCases?.map((caseId) => {
        const data = cases?.find((c) => c.id === caseId);
        return data || {};
      });
      return !!_cases?.some((item) => item?.status === caseStatusOptions.completed);
    }
    return false;
  }, [cases, checkedCases]);

  const renderTable = () => {
    switch (activeTab) {
      case 0:
      default:
        return (
          <CasesTable
            cases={casesList}
            surgeons={surgeons}
            hospitals={hospitals}
            procedures={procedures}
            onSelectCase={onSelectCase}
            handleCheck={handleCheck}
            checkedCases={checkedCases}
            handleCheckAll={handleCheckAll}
            pagination
            checkboxes={false}
            order={order}
            setOrder={(value) => dispatch(setOrder(value))}
            orderBy={orderBy}
            setOrderBy={(value) => dispatch(setOrderBy(value))}
            page={page}
            setPage={(value) => dispatch(setPage(value))}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
            loading={casesLoading}
            totalRows={totalRows}
          />
        );
      case 1:
        return (
          <ConnectedCasesTable
            cases={connectedCases}
            onSelectCase={(item) => onSelectCase(item, true)}
            handleCheck={handleCheck}
            checkedCases={checkedCases}
            handleCheckAll={handleCheckAll}
            pagination
            checkboxes
            tenants={tenants}
            order={order}
            setOrder={(value) => dispatch(setOrder(value))}
            orderBy={orderBy}
            setOrderBy={(value) => dispatch(setOrderBy(value))}
            page={page}
            setPage={(value) => dispatch(setPage(value))}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
            loading={casesLoading}
            totalRows={totalConnectedRows}
          />
        );
    }
  };

  const states = useMemo(() => {
    const list = [];
    hospitals?.forEach((hospital) => {
      if (!list?.includes(hospital.state)) {
        list.push(hospital.state);
      }
    });
    return [
      { key: 'all', label: 'All', value: '' },
      ...list?.map((state, index) => ({
        key: `${state}_${index}`,
        label: state,
        value: state,
      }))
    ];
  }, [hospitals]);

  return (
    <div className='page-container'>
      {(loading || fetching) && <LoadScreen /> }
      <span className='page-title'>Cases</span>
      <div className='cases-page__body'>
        <div className='filters-container'>
          <CasesFilters
            onAddCaseClick={onAddCaseClick}
            view={view}
            setView={setView}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
            caseWriteDisabled={caseWriteDisabled}
            showCanceled={showCanceled}
            onToggle={onToggle}
            disabled={isDisabled}
          />

          <KitFiltersList
            onClick={() => toggleModal(true)}
            hideShipped={hideShipped}
            hideReturned={hideReturned}
            hideConsignment={hideConsignment}
            hideLoan={hideLoan}
            hideClosed={hideClosed}
            showCanceled={showCanceled}
            activeTab={activeTab}
            surgeons={filters?.surgeons}
            hospitals={filters?.hospitals}
            states={filters?.states}
            surgeonsList={surgeons}
            hospitalsList={hospitals}
            onDelete={onRemoveFilter}
          />

          {/*{!!statusFilter.completed && !completedLoaded && (*/}
          {/*  <div className='d-flex flex-center p-b-lg'>*/}
          {/*    <CircularProgress />*/}
          {/*  </div>*/}
          {/*)}*/}

          {view === views.CALENDAR ? (
            <CasesCalendarView
              cases={casesList}
              onSelectCase={(id) => history.push(`${routes.CASES}/${id}`)}
              hospitals={hospitals}
              surgeons={surgeons}
              procedures={procedures}
              onDateChange={onDateChange}
            />
          ) : (
            userRole === userRoles.SALES_REP.name ? (
              <Tabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={onTabChange}
              >
                <div className='m-t-lg'>
                  {renderTable()}
                </div>
              </Tabs>
            ) : (
              <CasesTable
                cases={casesList}
                surgeons={surgeons}
                hospitals={hospitals}
                procedures={procedures}
                onSelectCase={onSelectCase}
                handleCheck={handleCheck}
                checkedCases={checkedCases}
                handleCheckAll={handleCheckAll}
                pagination
                checkboxes={false}
                order={order}
                setOrder={(value) => dispatch(setOrder(value))}
                orderBy={orderBy}
                setOrderBy={(value) => dispatch(setOrderBy(value))}
                page={page}
                setPage={(value) => dispatch(setPage(value))}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
                totalRows={totalRows}
                loading={casesLoading}
              />
            )
          )}
        </div>
        {cases && view === views.TABLE && (
          <div className='cases-form__container'>
            <Input
              type='search'
              placeholder='Search Cases (Patient ref or Case #)'
              value={search}
              onChange={handleSearch}
            />
            <div className='calendar__container'>
              <div className='font-bold m-l-lg m-t-sm m-b-sm'>
                Filter Cases
              </div>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DatePicker
                  autoOk
                  variant='static'
                  value={selectedDate ? moment(selectedDate) : null}
                  onChange={onChangeMinimizedCalendarDate}
                  onMonthChange={onMonthChange}
                  renderDay={renderDay}
                />
              </MuiPickersUtilsProvider>
              <div className='clear-button__container'>
                <Button
                  type='submit'
                  text='Clear date'
                  background='#0B0B0B'
                  onClick={() => {
                    dispatch(setPage(0));
                    dispatch(setSelectedDate(null));
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <CaseModal
        open={caseModalOpen}
        onClose={() => toggleCaseModal(false)}
        mode='create'
        hospitals={hospitals}
        surgeons={surgeons}
        procedures={procedures}
        procedureOptions={procedureOptions}
        products={products}
        kits={kits}
        onSubmit={onAddCase}
        userRole={userRole}
        userAddresses={userAddresses}
        flows={flows}
        sets={sets}
        currentTenant={currentTenant}
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteCases}
        title='Are you sure you want to delete these cases?'
        submitText='Delete'
        loading={isDeleting}
      />

      <AvailableKitsModal
        open={!!availableModalData}
        onClose={() => setAvailableModalData(null)}
        kitsList={kits}
        kits={availableModalData?.kits || []}
        onContinueClick={() => onContinueClick(availableModalData?.id)}
      />

      <KitsFiltersModal
        title='Filter Cases'
        open={isOpen}
        onClose={() => toggleModal(false)}
        activeTab={activeTab}
        hideShipped={hideShipped}
        hideReturned={hideReturned}
        hideConsignment={hideConsignment}
        hideLoan={hideLoan}
        hideClosed={hideClosed}
        showCanceled={showCanceled}
        toggleCanceled={(value) => dispatch(toggleCanceled(value))}
        toggleClosed={(value) => dispatch(toggleClosed(value))}
        toggleShipped={() => dispatch(toggleShipped())}
        toggleReturned={() => dispatch(toggleReturned())}
        toggleConsignment={() => dispatch(toggleConsignment())}
        toggleLoan={() => dispatch(toggleLoan())}
        selectedSurgeons={filters.surgeons}
        selectedHospitals={filters.hospitals}
        selectedStates={filters.states}
        onSurgeonChange={(values) => dispatch(setFilters({ surgeons: values }))}
        onHospitalChange={(values) => dispatch(setFilters({ hospitals: values }))}
        onStateChange={(values) => dispatch(setFilters({ states: values }))}
        surgeons={surgeons}
        hospitals={hospitals}
        states={states}
      />
    </div>
  );
};

export default withFirebase(CasesPage);
